<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog documents-create-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="documents-create-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="documents-create-modal-title">{{ isEditMode ? text.createModal.titleEdit : text.createModal.titleCreate }}</h3>
          <p>{{ isEditMode ? text.createModal.subtitleEdit : text.createModal.subtitleCreate }}</p>
        </div>
        <button class="app-modal__close" type="button" :aria-label="text.createModal.closeAriaLabel" @click="emitClose">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__content">
        <form class="app-modal__form" @submit.prevent="emitSubmit">
          <section class="app-modal__section">
            <div class="app-modal__grid two-columns documents-create-modal__grid">
              <label v-if="!showNameAsText" class="app-modal-field documents-create-modal__field-full">
                <span>{{ text.createModal.labels.name }}</span>
                <input
                  v-model.trim="form.name"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': v$.form.name.$error }"
                  maxlength="120"
                  autocomplete="off"
                  :placeholder="text.createModal.placeholders.name"
                  @blur="v$.form.name.$touch()"
                />
                <div v-if="v$.form.name.$error" class="invalid-feedback d-block">
                  {{ v$.form.name.$errors[0].$message }}
                </div>
              </label>

              <div v-else class="app-modal-field documents-create-modal__field-full documents-create-modal__record-title-wrap">
                <span>{{ text.createModal.labels.record }}</span>
                <h4 class="documents-create-modal__record-title">{{ form.name || text.common.emptyValue }}</h4>
              </div>

              <div v-if="!isEditMode && showSourceSelector && allowUrlSource" class="app-modal-field documents-create-modal__field-full">
                <span>{{ text.createModal.labels.source }}</span>
                <div class="documents-create-modal__source-options">
                  <label class="app-modal-toggle documents-create-modal__source-option">
                    <input
                      type="checkbox"
                      :checked="isFileSource"
                      @change="selectSource('file')"
                    />
                    <span>{{ text.createModal.sourceOptions.file }}</span>
                  </label>

                  <label class="app-modal-toggle documents-create-modal__source-option">
                    <input
                      type="checkbox"
                      :checked="isUrlSource"
                      @change="selectSource('url')"
                    />
                    <span>{{ text.createModal.sourceOptions.url }}</span>
                  </label>
                </div>
              </div>

              <div v-if="!isEditMode && isFileSource" class="app-modal-field documents-create-modal__field-full">
                <span>{{ text.createModal.labels.file }}</span>
                <div class="documents-create-modal__file-upload" :class="{ 'is-invalid': v$.form.file.$error }">
                  <input
                    ref="fileInput"
                    type="file"
                    class="documents-create-modal__file-input"
                    @change="handleFileChange"
                  />
                  <button
                    type="button"
                    class="app-header-btn app-header-btn--secondary documents-create-modal__file-trigger"
                    @click="openFilePicker"
                  >
                    {{ text.createModal.buttons.selectFile }}
                  </button>
                  <span class="documents-create-modal__file-name">
                    {{ form.fileName || text.createModal.placeholders.noFileSelected }}
                  </span>
                </div>
                <div v-if="v$.form.file.$error" class="invalid-feedback d-block">
                  {{ v$.form.file.$errors[0].$message }}
                </div>
              </div>

              <label v-if="!isEditMode && isUrlSource" class="app-modal-field documents-create-modal__field-full">
                <span>{{ text.createModal.labels.url }}</span>
                <input
                  v-model.trim="form.url"
                  type="url"
                  class="form-control"
                  :class="{ 'is-invalid': v$.form.url.$error }"
                  maxlength="400"
                  autocomplete="off"
                  :placeholder="text.createModal.placeholders.url"
                  @blur="v$.form.url.$touch()"
                />
                <div v-if="v$.form.url.$error" class="invalid-feedback d-block">
                  {{ v$.form.url.$errors[0].$message }}
                </div>
              </label>

              <label v-if="showValidityFields" class="app-modal-field">
                <span>{{ text.createModal.labels.validityStart }}</span>
                <VueDatePicker
                  v-model="validityStartModel"
                  class="app-modal-datepicker"
                  menu-class-name="app-modal-datepicker-menu"
                  :placeholder="text.createModal.placeholders.date"
                  :enable-time-picker="false"
                  auto-apply
                  locale="es-MX"
                  format="dd/MM/yyyy"
                  hide-input-icon
                  @update:model-value="touchDateField('validityStart')"
                />
                <div v-if="v$.form.validityStart.$error" class="invalid-feedback d-block">
                  {{ v$.form.validityStart.$errors[0].$message }}
                </div>
              </label>

              <label v-if="showValidityFields" class="app-modal-field">
                <span>{{ text.createModal.labels.validityEnd }}</span>
                <VueDatePicker
                  v-model="validityEndModel"
                  class="app-modal-datepicker"
                  menu-class-name="app-modal-datepicker-menu"
                  :placeholder="text.createModal.placeholders.date"
                  :enable-time-picker="false"
                  auto-apply
                  locale="es-MX"
                  format="dd/MM/yyyy"
                  hide-input-icon
                  @update:model-value="touchDateField('validityEnd')"
                />
                <div v-if="v$.form.validityEnd.$error" class="invalid-feedback d-block">
                  {{ v$.form.validityEnd.$errors[0].$message }}
                </div>
              </label>

              <label v-if="showCommentsField" class="app-modal-field documents-create-modal__field-full">
                <span>{{ text.createModal.labels.comments }}</span>
                <textarea
                  v-model.trim="form.comments"
                  class="form-control documents-create-modal__comments"
                  rows="5"
                  maxlength="500"
                  :placeholder="text.createModal.placeholders.comments"
                ></textarea>
              </label>
            </div>
          </section>

          <footer class="app-modal__footer">
            <div class="app-modal__footer-right">
              <button type="button" class="btn btn-light" @click="emitClose">{{ text.createModal.buttons.cancel }}</button>
              <button type="submit" class="btn btn-primary">{{ text.createModal.buttons.save }}</button>
            </div>
          </footer>
        </form>
      </div>
    </section>
  </div>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import { useVuelidate } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { parsePresentationDate, formatDateForApi } from '@/utils/datePresentation';
import { useDomainText } from '@/composables/text/useDomainText';
import '@vuepic/vue-datepicker/dist/main.css';

const { catalog: text } = useDomainText('documents');

export default {
  name: 'DocumentsCreateModal',
  components: {
    VueDatePicker,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    initialData: {
      type: Object,
      default: null,
    },
    showNameAsText: {
      type: Boolean,
      default: false,
    },
    showSourceSelector: {
      type: Boolean,
      default: true,
    },
    allowUrlSource: {
      type: Boolean,
      default: true,
    },
    showValidityFields: {
      type: Boolean,
      default: true,
    },
    showCommentsField: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['close', 'submit'],
  setup() {
    const v$ = useVuelidate();
    return { v$ };
  },
  data() {
    return {
      text,
      form: this.buildInitialForm(),
    };
  },
  validations() {
    return {
      form: {
        name: {
          required: helpers.withMessage(
            this.text.createModal.validation.nameRequired,
            (value) => String(value || '').trim() !== ''
          ),
        },
        file: {
          required: helpers.withMessage(
            this.text.createModal.validation.fileRequired,
            () => {
              if (this.isEditMode || !this.isFileSource) {
                return true;
              }

              return !!this.form.file;
            }
          ),
        },
        url: {
          required: helpers.withMessage(
            this.text.createModal.validation.urlRequired,
            (value) => {
              if (this.isEditMode || !this.isUrlSource) {
                return true;
              }

              return String(value || '').trim() !== '';
            }
          ),
        },
        validityStart: {
          dateRange: helpers.withMessage(
            this.text.createModal.validation.startDateAfterEnd,
            (value) => {
              const start = String(value || '').trim();
              const end = String(this.form.validityEnd || '').trim();

              if (!start || !end) {
                return true;
              }

              return start <= end;
            }
          ),
        },
        validityEnd: {
          dateRange: helpers.withMessage(
            this.text.createModal.validation.endDateBeforeStart,
            (value) => {
              const end = String(value || '').trim();
              const start = String(this.form.validityStart || '').trim();

              if (!start || !end) {
                return true;
              }

              return end >= start;
            }
          ),
        },
      },
    };
  },
  computed: {
    isEditMode() {
      return String(this.mode || '').trim().toLowerCase() === 'edit';
    },
    isFileSource() {
      return this.form.sourceType === 'file';
    },
    isUrlSource() {
      return this.allowUrlSource && this.form.sourceType === 'url';
    },
    validityStartModel: {
      get() {
        return parsePresentationDate(this.form.validityStart);
      },
      set(value) {
        this.form.validityStart = formatDateForApi(value);
      },
    },
    validityEndModel: {
      get() {
        return parsePresentationDate(this.form.validityEnd);
      },
      set(value) {
        this.form.validityEnd = formatDateForApi(value);
      },
    },
  },
  watch: {
    show(visible) {
      if (visible) {
        this.hydrateFormFromInitialData();
        this.v$.$reset();
        return;
      }

      this.form = this.buildInitialForm();
      this.v$.$reset();
    },
    initialData() {
      if (!this.show) {
        return;
      }

      this.hydrateFormFromInitialData();
      this.v$.$reset();
    },
  },
  methods: {
    buildInitialForm() {
      return {
        name: '',
        sourceType: 'file',
        file: null,
        fileName: '',
        url: '',
        validityStart: '',
        validityEnd: '',
        comments: '',
      };
    },
    hydrateFormFromInitialData() {
      if (!this.initialData || typeof this.initialData !== 'object') {
        this.form = this.buildInitialForm();
        return;
      }

      if (!this.isEditMode) {
        const startDate = String(this.initialData?.validityStart || '').trim();
        const endDate = String(this.initialData?.validityEnd || '').trim();

        this.form = {
          ...this.buildInitialForm(),
          name: String(this.initialData?.name || this.initialData?.fileName || '').trim(),
          validityStart: this.showValidityFields && startDate !== '' && startDate !== '-' ? startDate : '',
          validityEnd: this.showValidityFields && endDate !== '' && endDate !== '-' ? endDate : '',
          comments: this.showCommentsField ? String(this.initialData?.comments || '').trim() : '',
        };
        return;
      }

      const sourceType = this.allowUrlSource && Number(this.initialData?.isFile || 0) !== 1 ? 'url' : 'file';
      const startDate = String(this.initialData?.validityStart || '').trim();
      const endDate = String(this.initialData?.validityEnd || '').trim();

      this.form = {
        name: String(this.initialData?.name || '').trim(),
        sourceType,
        file: null,
        fileName: sourceType === 'file' ? this.text.createModal.placeholders.currentFile : '',
        url: sourceType === 'url'
          ? String(this.initialData?.documentRaw || this.initialData?.documentUrl || '').trim()
          : '',
        validityStart: this.showValidityFields && startDate !== '' && startDate !== '-' ? startDate : '',
        validityEnd: this.showValidityFields && endDate !== '' && endDate !== '-' ? endDate : '',
        comments: this.showCommentsField ? String(this.initialData?.comments || '').trim() : '',
      };
    },
    selectSource(type) {
      if (type !== 'file' && type !== 'url') {
        return;
      }

      if (!this.allowUrlSource && type === 'url') {
        return;
      }

      this.form.sourceType = type;
      this.form.file = null;
      this.form.fileName = this.isEditMode && type === 'file' ? this.text.createModal.placeholders.currentFile : '';
      this.form.url = '';
      this.v$.form.file.$reset();
      this.v$.form.url.$reset();
    },
    handleFileChange(event) {
      const [file] = event?.target?.files || [];
      this.form.file = file || null;
      this.form.fileName = file?.name || '';
      this.v$.form.file.$touch();
    },
    openFilePicker() {
      const input = this.$refs.fileInput;
      if (input && typeof input.click === 'function') {
        input.click();
      }
    },
    emitClose() {
      this.$emit('close');
    },
    touchDateField(field) {
      if (!this.v$.form[field]) {
        return;
      }

      this.v$.form[field].$touch();
    },
    emitSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      this.$emit('submit', {
        name: String(this.form.name || '').trim(),
        sourceType: this.form.sourceType,
        file: this.form.file,
        fileName: this.form.fileName,
        url: String(this.form.url || '').trim(),
        validityStart: this.showValidityFields ? String(this.form.validityStart || '').trim() : '',
        validityEnd: this.showValidityFields ? String(this.form.validityEnd || '').trim() : '',
        comments: this.showCommentsField ? String(this.form.comments || '').trim() : '',
      });
    },
  },
};
</script>
